import { ReactNode, useState } from 'react';
import { useDispatch } from 'react-redux';
import { generalOpenToastError } from '../../../../actions/general.actions';
import { CAMPOS_CODIGO, CampoCodigo, Codigos, CODIGOS_VACIOS, OPCIONES_CODIGOS, PREGUNTAS_CODIGO, esCodigoValido } from '../../../../utils/codificacion-compras.utils';

interface PanelProps {
  // false = periodo anterior a febrero 2024: las 4 columnas van en 0 y no hay nada que elegir
  exige: boolean;
  total: number;
  pendientes: number;
  // Aplica lo elegido a las compras y devuelve el mensaje con el resultado ("Se aplicó a N compras.")
  onAplicar: (masivo: Codigos, soloSinClasificar: boolean) => string;
  // Contenido extra al pie del panel (ej. boton "Guardar cambios" en "Ver libro")
  pie?: ReactNode;
}

// Bloque "Clasificación de las compras": se eligen los 4 datos una vez y se aplican a todas las compras o solo a las pendientes.
// Lo usan "Validar datos" (carga nueva) y "Ver libro" (libro ya generado).
export const PanelClasificacionCompras = ({ exige, total, pendientes, onAplicar, pie }: PanelProps) => {
  const dispatch = useDispatch();
  const [masivo, setMasivo] = useState<Codigos>(CODIGOS_VACIOS);
  const [aviso, setAviso] = useState<string>('');

  const aplicar = (soloSinClasificar: boolean) => {
    if (!CAMPOS_CODIGO.some((campo) => masivo[campo] > 0)) {
      setAviso('');
      dispatch(generalOpenToastError('Elija al menos un dato para poder aplicarlo a las compras'));
      return;
    }
    if (soloSinClasificar && pendientes === 0) {
      setAviso('No hay compras pendientes: no se cambió nada.');
      return;
    }
    setAviso(onAplicar(masivo, soloSinClasificar));
  };

  return (
    <div style={{ border: '1px solid #e6e6e6', padding: '12px', marginBottom: '16px' }}>
      <h4 style={{ margin: '0 0 4px' }}>Clasificación de las compras</h4>
      <p className="s" style={{ margin: '0 0 8px' }}>
        {exige
          ? 'Cada compra necesita estos 4 datos. Elíjalos aquí y aplíquelos a las compras, o cámbielos compra por compra en la tabla.'
          : 'Para períodos anteriores a febrero 2024 estas columnas se declaran con 0.'}
      </p>
      {exige && (
        <>
          <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap', alignItems: 'flex-start' }}>
            {CAMPOS_CODIGO.map((campo) => (
              <label key={campo} style={{ display: 'flex', flexDirection: 'column', fontSize: '12px', gap: '3px' }}>
                <b>{OPCIONES_CODIGOS[campo].etiqueta}</b>
                <span style={{ fontWeight: 'normal', color: '#4a5562' }}>{PREGUNTAS_CODIGO[campo]}</span>
                <select value={masivo[campo]} onChange={(e) => { setAviso(''); setMasivo((prev) => ({ ...prev, [campo]: Number(e.target.value) })); }}
                  style={{ minWidth: '200px', padding: '6px', border: '1px solid #ccc', borderRadius: '4px' }}>
                  <option value={0}>— Sin cambio —</option>
                  {OPCIONES_CODIGOS[campo].opciones.map((op) => <option key={op.valor} value={op.valor}>{op.texto}</option>)}
                </select>
              </label>
            ))}
          </div>
          <div style={{ display: 'flex', gap: '24px', flexWrap: 'wrap', marginTop: '12px' }}>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
              <button className="button w-auto" onClick={() => aplicar(false)}>Aplicar a todas las compras</button>
              <span className="s" style={{ color: '#4a5562' }}>Reemplaza lo que ya tengan.</span>
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
              <button className="button w-auto transparent" onClick={() => aplicar(true)}>Aplicar solo a las pendientes</button>
              <span className="s" style={{ color: '#4a5562' }}>No cambia las que ya están listas.</span>
            </div>
          </div>
          <p className="s" style={{ margin: '12px 0 0', display: 'flex', flexWrap: 'wrap', gap: '14px', alignItems: 'center' }}>
            {pendientes > 0
              ? <>
                  <b style={{ color: '#7a4b00' }}>{pendientes} {pendientes === 1 ? 'compra pendiente' : 'compras pendientes'} de clasificar</b>
                  <span style={{ color: '#4a5562' }}>{total - pendientes} de {total} {total === 1 ? 'lista' : 'listas'}</span>
                </>
              : <b style={{ color: '#0b6b57' }}>Todas las compras están clasificadas ({total} de {total})</b>}
            {aviso && <span role="status" style={{ color: '#3c4756' }}>· {aviso}</span>}
          </p>
        </>
      )}
      {pie}
    </div>
  );
};

interface SelectProps {
  campo: CampoCodigo;
  valor: number;
  exige: boolean;
  onChange: (valor: number) => void;
}

// Selector de un dato en una fila de la tabla; se marca cuando falta (solo si el periodo lo exige).
// Es un componente de modulo (no se define dentro de otro) para que no se vuelva a montar en cada render.
export const SelectClasificacion = ({ campo, valor, exige, onChange }: SelectProps) => {
  const invalido = exige && !esCodigoValido(campo, valor);
  return (
    <select value={valor} onChange={(e) => onChange(Number(e.target.value))}
      aria-label={OPCIONES_CODIGOS[campo].etiqueta}
      style={{ minWidth: '120px', padding: '4px', border: invalido ? '1px solid #d93025' : '1px solid #ccc', borderRadius: '4px' }}>
      <option value={0}>{exige ? 'Seleccione…' : '0'}</option>
      {OPCIONES_CODIGOS[campo].opciones.map((op) => <option key={op.valor} value={op.valor}>{op.texto}</option>)}
    </select>
  );
};
